import moment from "moment";
import FileSaver from "file-saver";
import { Buffer, Cell, Column, Row, TableColumnProperties, Workbook, Worksheet } from "exceljs";
import User from "./user";
import Msg from "./message";
import Formula from "./formula";
import Format from "./format";
import Field from "./field";
import ExcelExportTemplate from "./excel-export-template";
import Entity from "./entity";
import Documents from "./documents";
import DocumentValue from "./document-value";
import Application from "./application";
import { clearLinebreaks, flatten, replaceAll } from "../utils";
import IRelationPathField from "../interface/relation-path-field";
import IRelationPath from "../interface/relation-path";
import IConfigNumber from "../interface/field-cfg-number";
import IConfigFormula from "../interface/field-cfg-formula";
import IConfigDatetime from "../interface/field-cfg-datetime";
import IField from "../interface/field";
import IExcelSheet from "../interface/excel-sheet";
import IExcelExportField from "../interface/excel-export-field";
import IExcelExport from "../interface/excel-export";
import IExcelColumn from "../interface/excel-column";
import IEntity from "../interface/entity";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import Global from "../global";
import Message from "../enum/message";
import FormulaType from "../enum/formula-type";
import { ExcelExportTypes } from "../enum/field-types";
import FieldType from "../enum/field-type";
import ErrorCode from "../enum/error-code";
import CurrencyPosition from "../enum/currency-position";

class ExcelExportSingleton {

    /* Public Methods */

    public async global(): Promise<void> {
        const entities: IEntity[] = Entity.getAll();
        const sheets: IExcelSheet[] = []
        for (let i = 0; i < entities.length; i++) {
            const tpl: IExcelExport = this.getEmptyTemplate(entities[i]);
            const docs: IDocument[] = await Documents.loadDocuments(entities[i].id);
            await Documents.loadRelatedDocs(entities[i], docs);
            docs.forEach((doc: IDocument) => Formula.processDocument(entities[i], doc));
            sheets.push({ tpl, docs });
        }
        await this.generateFile(this.getGlobalFileName(), sheets);
    }

    public async tpl(tpl: IExcelExport, docs: IDocument[]): Promise<void> {
        if (User.isDesigner()) {
            if (tpl.id) {
                await ExcelExportTemplate.update(tpl);
            } else {
                await ExcelExportTemplate.create(tpl);
            }
        }
        return await this.generateFile(tpl.name, [{ tpl, docs }]);
    }

    public getFields(id: number): IRelationPath[] {
        return Field.getRelationPaths(id, ExcelExportTypes, 1, [], true);
    }

    /* Private Methods */

    private async generateFile(fileName: string, sheets: IExcelSheet[]): Promise<void> {
        const wb: Workbook = new Workbook();
        sheets.forEach((sheet: IExcelSheet, index: number) => {
            if (sheet.tpl.fields.length) {
                try {
                    const ws: Worksheet = wb.addWorksheet(this.getSheetName(sheet.tpl.name, wb));
                    this.buildTable(ws, sheet.tpl, sheet.docs);
                    ws.eachRow((row: Row, index: number) => {
                        row.font = {
                            name: Global.EXCEL_FONT_NAME,
                            size: Global.EXCEL_FONT_SIZE,
                            bold: index === 1
                        };
                    });
                } catch (e) {
                    console.log(`error on building sheet ${sheet.tpl.name} (index: ${index})`, sheet);
                    console.error(e);
                    throw ErrorCode.LibInternal;
                }
            }
        });
        try {
            const buffer: Buffer = await wb.xlsx.writeBuffer();
            FileSaver.saveAs(new Blob([buffer], {
                type: Global.EXCEL_BLOB_TYPE
            }), this.getFileName(fileName));
        } catch (e) {
            throw ErrorCode.LibInternal;
        }
    }

    /* Private Methods - Build Table */

    private buildTable(ws: Worksheet, tpl: IExcelExport, docs: IDocument[]): void {
        const cols: IExcelColumn[] = this.getColumns(tpl);
        let totalsFirstIndex: number = -1;
        for (let i = 0; i < cols.length; i++) {
            if (this.hasTotals(cols[i].field, docs)) {
                totalsFirstIndex = i;
                break;
            }
        }
        const empty: boolean = totalsFirstIndex === 0;
        ws.addTable({
            name: `Table_${('000' + ws.id).slice(-4)}`,
            ref: 'A1',
            headerRow: true,
            totalsRow: totalsFirstIndex > -1,
            columns: this.getTableColumns(cols, docs, empty),
            rows: this.getTableRows(cols, docs, empty),
            style: {
                theme: 'TableStyleLight1',
                showRowStripes: true,
            }
        });
        cols.forEach((col: IExcelColumn, index: number) => {
            const add: number = empty ? 2 : 1;
            const format: string | null = this.getFormat(col.field);
            if (format) {
                ws.getColumn(index + add).numFmt = format;
            }
        });
        this.autosize(ws, empty);
    }

    private getTableColumns(cols: IExcelColumn[], docs: IDocument[], empty: boolean): TableColumnProperties[] {
        const result: TableColumnProperties[] = [];
        if (empty) {
            result.push({ name: ' ' });
        }
        cols.forEach((col: IExcelColumn) => {
            const res: TableColumnProperties = { name: col.name };
            if (this.hasTotals(col.field, docs)) {
                res.totalsRowFunction = 'sum';
                res.totalsRowLabel = Msg.get(Message.timeonly);
            }
            result.push(res);
        });
        return result;
    }

    private getTableRows(cols: IExcelColumn[], docs: IDocument[], empty: boolean): (IDocumentValue | Date)[][] {
        const result: (IDocumentValue | Date)[][] = [];
        if (docs.length) {
            for (let i = 0; i < docs.length; i++) {
                result.push(this.getTableRow(cols, docs[i], empty));
            }
        } else {
            result.push(new Array(cols.length));
        }
        return result;
    }

    private getTableRow(cols: IExcelColumn[], doc: IDocument, empty: boolean): (IDocumentValue | Date)[] {
        const result: (IDocumentValue | Date)[] = [];
        if (empty) {
            result.push(' ');
        }
        cols.forEach((col: IExcelColumn) => {
            let value: IDocumentValue = DocumentValue.getByPathSync(doc, col.pathId, col.field);
            if (col.field.uiType === FieldType.datetime || col.field.uiType === FieldType.check) {
                result.push(Format.byField(value, col.field));
            } else if (col.field.uiType === FieldType.formula) {
                if (typeof value === 'number') {
                    result.push(value);
                } else {
                    result.push(Format.byField(value, col.field));
                }
            } else {
                result.push(value);
            }
        });
        return result;
    }

    /* Private Methods */

    private getGlobalFileName(): string {
        const timestamp: string = moment().format(Global.EXCEL_GLOBAL_NAME_TIME_FORMAT);
        const appName: string = clearLinebreaks(Application.getName() || '').trim();
        return `${appName}_${timestamp}`;
    }

    private getSheetName(name: string, wb: Workbook): string {
        let result: string = clearLinebreaks(name).replace(/[\\\:\/\?\*\[\]]/g, ' ').trim();
        if (!result) {
            result = Msg.get(Message.emptysheet);
        }
        const names: string[] = wb.worksheets.map((ws: Worksheet) => ws.name);
        result = this.getNextName(result, names);
        if (result.length > Global.EXCEL_SHEET_MAX_LENGTH) {
            const s: number = Global.EXCEL_SHEET_MAX_LENGTH - Global.EXCEL_SHEET_OVERFLOW.length;
            return result.slice(0, s) + Global.EXCEL_SHEET_OVERFLOW;
        }
        return result;
    }

    private getFileName(name: string): string {
        const result: string = replaceAll(clearLinebreaks(name), ' ', '_').trim();
        if (result) {
            return result;
        } else {
            return Global.EXCEL_DEFAULT_FILE_NAME;
        }
    }

    private getColumnName(name: string): string {
        return clearLinebreaks(name).trim();
    }

    private getNextName(name: string, names: string[]): string {
        const add: number = 2;
        if (names.includes(name)) {
            const current: number = parseInt(name.slice(add * -1));
            if (!isNaN(current)) {
                const suffix: string = ('0'.repeat(add) + (current + 1)).slice(add * -1);
                return this.getNextName(`${name.slice(0, add * -1)}${suffix}`, names);
            }
            return this.getNextName(`${name} ${('0'.repeat(add) + 1).slice(add * -1)}`, names);
        }
        return name;
    }

    private getEmptyTemplate(entity: IEntity): IExcelExport {
        const fields: IExcelExportField[] = [];
        this.getFields(entity.id).forEach((g: IRelationPath) => {
            const gFields: IExcelExportField[] = g.fields.map((f: IRelationPathField) => {
                return { pathId: f.pathId };
            });
            fields.push.apply(fields, gFields);
        });
        return {
            name: entity.singular,
            entityId: entity.id,
            fields
        };
    }

    private getColumns(tpl: IExcelExport): IExcelColumn[] {
        const result: IExcelColumn[] = [];
        const paths: IRelationPath[] = this.getFields(tpl.entityId);
        const fields: IRelationPathField[] = flatten(paths.map((g: IRelationPath) => g.fields));
        const names: string[] = [];
        tpl.fields.forEach((f: IExcelExportField) => {
            const field: IField | null = Field.get(f.pathId[f.pathId.length - 1]);
            if (field) {
                const id: string = f.pathId.toString();
                const rel: IRelationPathField | null = fields.find((f: IRelationPathField) => {
                    return f.id === id;
                }) || null;
                if (rel) {
                    // This replace breaklines
                    const n: string = this.getColumnName(rel.pathName);
                    const name: string = this.getNextName(n, names);
                    result.push({ id, field, pathId: rel.pathId, name });
                    names.push(name);
                }
            }
        });
        return result;
    }

    private autosize(sheet: Worksheet, empty: boolean = false) {
        const canvas: HTMLCanvasElement = document.createElement('canvas');
        const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
        if (ctx) {
            const maxLengths: number[] = [];
            sheet.eachRow((row: Row) => {
                row.eachCell((cell: Cell, index) => {
                    if (typeof cell.value === 'string') {
                        if (typeof maxLengths[index] === 'undefined') {
                            maxLengths[index] = 0;
                        }
                        const fontSize: number = cell.font && cell.font.size ? cell.font.size : 11;
                        ctx.font = `${fontSize}pt ${Global.EXCEL_FONT_NAME}`;
                        const metrics: TextMetrics = ctx.measureText(cell.value);
                        maxLengths[index] = Math.max(maxLengths[index], metrics.width)
                    }
                })
            });
            for (let i = 1; i <= sheet.columnCount; i++) {
                const col: Partial<Column> = sheet.getColumn(i);
                if (maxLengths[i]) {
                    if (empty && i === 1) {
                        col.width = Global.EXCEL_TOTAL_COL_WIDTH;
                    } else {
                        const add: number = Global.EXCEL_COL_EXTRA_WIDTH;
                        col.width = maxLengths[i] / Global.EXCEL_PIXELS_PER_UNIT + add;
                    }
                    col.width = Math.max(col.width, Global.EXCEL_COL_MIN_WIDTH);
                    col.width = Math.min(col.width, Global.EXCEL_COL_MAX_WIDTH);
                }
            }
        }
    }

    private getFormat(field: IField): string | null {
        if (field.uiType === FieldType.datetime) {
            const config: IConfigDatetime = field.uiConfig as IConfigDatetime;
            return config.format;
        } else if (field.uiType === FieldType.number) {
            const format: IConfigNumber = field.uiConfig as IConfigNumber;
            return this.getNumberFormat(format);
        } else if (field.uiType === FieldType.formula) {
            const config: IConfigFormula = field.uiConfig as IConfigFormula;
            if (config.type === FormulaType.number) {
                const format: IConfigNumber = config.format as IConfigNumber;
                return this.getNumberFormat(format);
            }
        }
        return null;
    }

    private getNumberFormat(config: IConfigNumber): string {
        let result: string = `# ##${(0).toFixed(Math.abs(config.decimalLength))}`;
        if (config.currencySymbol) {
            const space: string = this.getCurrencySpace(config.currencySymbol);
            if (config.currencyPosition === CurrencyPosition.Left) {
                result = `[$${config.currencySymbol}${space}]${result}`;
            } else if (config.currencyPosition === CurrencyPosition.Right) {
                result = `${result}[$${space}${config.currencySymbol}]`;
            }
        }
        return result;
    }

    private getCurrencySpace(cur: string): string {
        return Global.EXCEL_NOSPACE_CURRENCIES.includes(cur) ? '' : ' ';
    }

    private hasTotals(field: IField, docs: IDocument[]): boolean {
        if (!docs.length) {
            return false;
        } else if (field.uiType === FieldType.number) {
            return true;
        } else if (field.uiType === FieldType.formula) {
            const config: IConfigFormula = field.uiConfig as IConfigFormula;
            if (config.type === FormulaType.number) {
                return true;
            }
        }
        return false;
    }

}

const ExcelExport = new ExcelExportSingleton();

export default ExcelExport;